/**
 * The offer, in words. One copy for both apps.
 *
 * These are claims about what the product is and what it costs, so the two
 * platforms must not be able to make different ones: a bullet edited on web and
 * missed on mobile advertises two different products for the same SKU.
 *
 * No prices live here. Mobile reads them off the store already formatted, web
 * off `/payments/prices` with the symbol to print them with — neither app
 * formats a price, and neither states one it was not given.
 */
// The yearly plan's discount, in words. Written out rather than derived from
// the two prices: it needs no currency either way, and prices move rarely
// enough that keeping it as copy is the simpler trade. Both apps read these, so
// a price change in the stores or Stripe means editing them here.
export const YEARLY_SAVING_PERCENTAGE = 33;
// The same discount as months of the year given back: 33% of twelve months is
// four. Web shows it via `billing.fourMonthsFree`; mobile reads it here.
export const YEARLY_MONTHS_FREE = "4 months free";
/**
 * Ultimate's headline benefits. All affirmative: Ultimate includes everything,
 * so there is nothing to withhold and no column to compare against.
 */
export const HEADLINE_BENEFITS = [
    {
        title: "Unlimited credits",
        info: "Remove all limits. Speed up the journey to your dream squad.",
    },
    { title: "No ads", info: "The best experience. Free from distractions." },
];
/** What Ultimate adds once a club is imported. */
export const CLUB_FEATURES = [
    { title: "SBC Solver", info: "Solve any SBC with your club in seconds" },
    {
        title: "My Evolutions",
        info: "Instant evo sync. See Meta Ratings and best evo paths",
    },
    { title: "My Club Overview", info: "See cards to sell for easy profit" },
    { title: "AI Squad Builder", info: "Find your club's best squads" },
    {
        title: "My Recommendations",
        info: "Best squad upgrades tailored to your budget, fodder and evos",
    },
];
/** The free tier's second bullet. The first one states the daily grant. */
export const FREE_PLAN_DAILY_INFO = "Access powerful AI features with your club. Solve SBCs, see the best chains with your available evos, build your most meta squad, and much more.";
export const FREE_PLAN_CLUB_BULLET = {
    title: "Club Insights, Profit, and Value",
    info: "Sync your entire club. Find easy profit, see your club value and potential coins, and explore your players and evolutions. All free, without limits.",
};
/**
 * The upsell panel inside the out-of-credits dialog. Same promise as the plan
 * cards above, in the same hand — the two surfaces are seen minutes apart.
 */
export const UPGRADE_BENEFITS = [
    "Unlimited credits, no waiting",
    "Every AI feature, instantly",
    "No ads",
];
/* -------------------------------------------------------------------------- */
/* The credits explainer, further down the same page                          */
/* -------------------------------------------------------------------------- */
export const CREDITS_EXPLAINER_HEADING = "How credits work";
export const CREDITS_EXPLAINER_WHAT = "Credits are used for AI actions that use your own club and require heavy computation.";
/** The accented half of the reassurance; the caller supplies the rest. */
export const CREDITS_EXPLAINER_FREE_ACCENT = "Everything else is free";
export const CREDITS_EXPLAINER_FREE_TAIL = "to use as much as you want.";
export const CREDITS_TABLE_HEADING = "What features cost credits";
/**
 * The free half of the cost table. Hardcoded rather than derived: these are not
 * features the backend prices, they are the answer to "what does this cost me",
 * and the last row is there so the list reads as complete rather than partial.
 */
export const FREE_ACTIONS = [
    "Import your club; players, evolutions and much more",
    "See profit in your club",
    "Club value, potential coins and other club stats",
    "AI recommendations in squad builder",
    "Everything else not mentioned",
];
/** The backend quotes every price; 1 is only used before it has answered. */
export const ASSUMED_COST = 1;
/**
 * The closing upsell. Framed on what the reader gains rather than on the limit
 * they just read about — the limit is ours, the squad is theirs.
 */
export const CREDITS_UPSELL_HEADING = "Want to get your dream Ultimate Team faster?";
export const CREDITS_UPSELL_BODY = "Ultimate gives you unlimited credits. Every AI feature, whenever you want it, with no daily limit.";
export const CREDITS_UPSELL_CTA = "Go Unlimited";
//# sourceMappingURL=planCopy.js.map